import styled, { css } from 'styled-components';
import { ThemeProps } from '@/styles/global';
import { StylesProps } from '@/utils/stylesProps';
import Autocomplete from 'react-google-autocomplete';

type Props = StylesProps & ThemeProps;

export const TagsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  border: 0.0625rem solid ${(props: ThemeProps) => props.theme.lightGrayColor};
  border-radius: 0.25rem;
  min-height: 3.5rem;
  padding: 0 1.5rem;
  outline: none;
  ${(props: Props) =>
    props.isEmpty &&
    css`
      border-color: red !important;
      & input::-webkit-input-placeholder {
        color: red;
      }
    `}
  &:focus-within {
    border-color: ${(props: ThemeProps) => props.theme.softGreenColor};
    box-shadow: 0 0 0.25rem rgba(89, 115, 114, 0.16);
  }
  padding: 0 0.5rem;
  & > div {
    flex: 1;
    font-weight: normal;
    font-size: 1.125rem;
    line-height: 1.6875rem;
    border: none;
    height: 3.5rem;
    &:focus {
      outline: transparent;
    }
  }
  @media (max-width: 768px) {
    min-height: 3rem;
    input {
      font-size: 1rem;
    }
  }
`;

export const TagsInputWrapper = styled.div`
  position: relative;
  width: 100%;
  min-width: 10.25rem;
  background-color: transparent;
  border-radius: 0.25rem;
  & > div {
    box-shadow: none;
  }
  & input {
    padding: 0 !important;
    border: none !important;
  }
  @media (max-width: 768px) {
    height: auto !important;
  }
`;

export const LocationWrapper = styled.div`
  width: 100%;
  & input {
    ${(props: Props) =>
      props.isEmpty &&
      css`
        border-color: red !important;
        &::-webkit-input-placeholder {
          color: red;
        }
      `}
  }
`;

export const LocationInput = styled(Autocomplete)`
  border: 0.0625rem solid ${(props: Props) => props.theme.lightGrayColor};
  border-radius: 0.25rem;
  font-weight: normal;
  font-size: 1.125rem;
  line-height: 1.6875rem;
  width: 100%;
  height: 3.5rem;
  padding: 0 1.5rem;
  outline: none;
  &:focus {
    border-color: ${(props: Props) => props.theme.softGreenColor};
    box-shadow: 0 0 0.25rem rgba(89, 115, 114, 0.16);
  }
  @media (max-width: 768px) {
    font-size: 1rem;
    height: 3rem;
  }
`;

export const TextEditorWrapper = styled.div`
  & > div {
    border: 0.0625rem solid ${(props: Props) => props.theme.lightGrayColor} !important;
    ${(props: Props) =>
      props.isEmpty &&
      css`
        border-color: red !important;
        & .ql-editor.ql-blank::before {
          color: red;
        }
      `}
    border-radius: 0.25rem;
    & > div {
      border: none !important;
      &:first-child {
        border-bottom: 0.0625rem solid
          ${(props: Props) =>
            props.isEmpty ? `red` : props.theme.lightGrayColor} !important;
      }
    }
    &:focus-within {
      border-color: ${(props: Props) => props.theme.softGreenColor} !important;
      & > div {
        &:first-child {
          border-color: ${(props: Props) =>
            props.theme.softGreenColor} !important;
        }
        & .ql-editor.ql-blank::before {
          color: ${(props: Props) => props.theme.grayColor};
        }
      }
    }
  }
  & .ql-editor.ql-blank::before {
    font-style: normal;
    color: ${(props: Props) => props.theme.grayColor};
  }
  & .ql-container {
    & .ql-editor {
      min-height: 20rem;
      font-family: Ubuntu;
      font-weight: normal;
      font-size: 1.125rem;
    }
  }
  .ql-snow.ql-toolbar button:hover,
  .ql-snow .ql-toolbar button:hover,
  .ql-snow.ql-toolbar button:focus,
  .ql-snow .ql-toolbar button:focus,
  .ql-snow.ql-toolbar button.ql-active,
  .ql-snow .ql-toolbar button.ql-active,
  .ql-snow.ql-toolbar .ql-picker-label:hover,
  .ql-snow .ql-toolbar .ql-picker-label:hover,
  .ql-snow.ql-toolbar .ql-picker-label.ql-active,
  .ql-snow .ql-toolbar .ql-picker-label.ql-active,
  .ql-snow.ql-toolbar .ql-picker-item:hover,
  .ql-snow .ql-toolbar .ql-picker-item:hover,
  .ql-snow.ql-toolbar .ql-picker-item.ql-selected,
  .ql-snow .ql-toolbar .ql-picker-item.ql-selected {
    color: ${(props: Props) => props.theme.softGreenColor} !important;
  }
  .ql-toolbar.ql-snow {
    font-family: ${(props: Props) => props.theme.fontFamily};
  }
  .ql-snow.ql-toolbar button:hover .ql-stroke,
  .ql-snow .ql-toolbar button:hover .ql-stroke,
  .ql-snow.ql-toolbar button:focus .ql-stroke,
  .ql-snow .ql-toolbar button:focus .ql-stroke,
  .ql-snow.ql-toolbar button.ql-active .ql-stroke,
  .ql-snow .ql-toolbar button.ql-active .ql-stroke,
  .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke,
  .ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke,
  .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
  .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke,
  .ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke,
  .ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke,
  .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
  .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
  .ql-snow.ql-toolbar button:hover .ql-stroke-miter,
  .ql-snow .ql-toolbar button:hover .ql-stroke-miter,
  .ql-snow.ql-toolbar button:focus .ql-stroke-miter,
  .ql-snow .ql-toolbar button:focus .ql-stroke-miter,
  .ql-snow.ql-toolbar button.ql-active .ql-stroke-miter,
  .ql-snow .ql-toolbar button.ql-active .ql-stroke-miter,
  .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
  .ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
  .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
  .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
  .ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
  .ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
  .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter,
  .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter {
    stroke: ${(props: Props) => props.theme.softGreenColor} !important;
  }
`;
